import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, MessageCircle, Settings as SettingsIcon, UserCheck, Layers, MessageSquare, Wallet,
  Rocket, DoorOpen, Monitor, ChevronsLeft, X, LogOut, Menu, BarChart3, Phone, FileText, BookUser,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ADMIN_TOKEN_KEY } from "@/lib/authApi";

const ITEMS = [
  { to: "/admin",                 label: "Dashboard",      icon: LayoutDashboard, tab: "dashboard" },
  { to: "/admin?tab=whatsapp",    label: "WhatsApp",       icon: MessageCircle,   tab: "whatsapp", badgeKey: "failed" },
  { to: "/admin?tab=settings",    label: "Settings",       icon: SettingsIcon,    tab: "settings" },
  { divider: "Workflows" },
  { to: "/admin/registrations",   label: "Registrations",  icon: UserCheck },
  { to: "/admin/directory",       label: "Member directory", icon: BookUser },
  { to: "/admin/statistics",      label: "Ledger",         icon: BarChart3 },
  { to: "/admin/voice",           label: "Voice",          icon: Phone },
  { to: "/admin/conclave",        label: "Conclave",       icon: Layers },
  { to: "/admin/chat-insights",   label: "Chat Insights",  icon: MessageSquare },
  { to: "/admin/payouts",         label: "Payouts",        icon: Wallet },
  { to: "/admin/recap",           label: "Recap",          icon: FileText },
  { to: "/admin/soft-launch",     label: "Soft launch",    icon: Rocket },
  { to: "/reception",             label: "Reception desk", icon: DoorOpen },
  { to: "/big-screen",            label: "Big screen",     icon: Monitor, external: true },
];

export function AdminShell({ children, user, onLogout, failedCount = 0 }) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem("bni-admin-sidebar-collapsed") === "1"; } catch { return false; }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Compute slide direction (left/right) based on sidebar order:
  // moving DOWN the sidebar slides the new page in from the right, UP slides from the left.
  const itemIndexFor = React.useCallback((pathname, search) => {
    const qs = new URLSearchParams(search).get("tab") || "dashboard";
    const i = ITEMS.findIndex((it) => it.divider ? false : (it.tab
      ? pathname === "/admin" && qs === it.tab
      : pathname === it.to));
    return i >= 0 ? i : 0;
  }, []);
  const currentIndex = itemIndexFor(location.pathname, location.search);
  const prevIndexRef = React.useRef(currentIndex);
  const direction = currentIndex >= prevIndexRef.current ? 1 : -1;
  useEffect(() => { prevIndexRef.current = currentIndex; }, [currentIndex]);

  useEffect(() => {
    try { localStorage.setItem("bni-admin-sidebar-collapsed", collapsed ? "1" : "0"); } catch {}
    document.documentElement.style.setProperty("--sb-w", collapsed ? "76px" : "240px");
  }, [collapsed]);
  useEffect(() => () => { document.documentElement.style.removeProperty("--sb-w"); }, []);
  useEffect(() => { setMobileOpen(false); }, [location.pathname, location.search]);

  // Preserve sidebar scroll across route changes.
  // Each admin page re-mounts <AdminShell>, so `.asb-nav` would otherwise
  // reset to scrollTop=0 on every navigation. On top of that, the browser
  // auto-scrolls the newly-focused Link into view RIGHT AFTER click (and
  // this fires BEFORE AdminShell unmounts, polluting sessionStorage).
  // We take control at the moment of the click: capture scrollTop first,
  // persist it, then suppress scroll saves for ~1s so neither the focus-
  // scroll nor our restore writes corrupt the saved value.
  const navRef = useRef(null);
  const scrollLockUntilRef = useRef(0);

  const onNavLinkMouseDown = React.useCallback(() => {
    const el = navRef.current;
    if (!el) return;
    try { sessionStorage.setItem("bni-admin-sidebar-scroll", String(el.scrollTop)); } catch {}
    scrollLockUntilRef.current = performance.now() + 900; // beats focus-scroll + remount + chase
  }, []);

  useLayoutEffect(() => {
    const el = navRef.current;
    if (!el) return;
    let target = 0;
    try { target = parseInt(sessionStorage.getItem("bni-admin-sidebar-scroll") || "0", 10) || 0; } catch {}
    let userActive = false;
    const yield_ = () => { userActive = true; };
    el.addEventListener("wheel", yield_, { passive: true });
    el.addEventListener("touchstart", yield_, { passive: true });
    if (el.scrollTop !== target) el.scrollTop = target;
    let rafId;
    const start = performance.now();
    const chase = () => {
      if (!el || userActive) return;
      if (el.scrollTop !== target) el.scrollTop = target;
      if (performance.now() - start < 500) rafId = requestAnimationFrame(chase);
    };
    rafId = requestAnimationFrame(chase);
    const onScroll = () => {
      if (performance.now() < scrollLockUntilRef.current) return; // ignore focus-scroll + chase writes
      try { sessionStorage.setItem("bni-admin-sidebar-scroll", String(el.scrollTop)); } catch {}
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      el.removeEventListener("wheel", yield_);
      el.removeEventListener("touchstart", yield_);
      el.removeEventListener("scroll", onScroll);
    };
  }, [location.pathname, location.search]);

  const logout = onLogout || (() => { localStorage.removeItem(ADMIN_TOKEN_KEY); navigate("/admin"); window.location.reload(); });
  const currentTab = new URLSearchParams(location.search).get("tab") || "dashboard";
  const isActive = (it) => it.tab
    ? location.pathname === "/admin" && currentTab === it.tab
    : location.pathname === it.to;

  return (
    <div className="min-h-screen">
      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
            onClick={() => setMobileOpen(false)} className="admin-sidebar-overlay" data-testid="admin-sidebar-overlay" />
        )}
      </AnimatePresence>

      <aside data-testid="admin-sidebar" aria-label="Admin navigation"
        className={`admin-sidebar ${collapsed ? "is-collapsed" : ""} ${mobileOpen ? "is-mobile-open" : ""}`}>
        <div className="asb-brand">
          <img src="/risse-bni-logo.png" alt="RISSE 26" className="asb-logo" />
          <div className="asb-brand-label">
            <div className="asb-brand-h">BNI Davangere</div>
            <div className="asb-brand-s">Members Day · Admin</div>
          </div>
          <button type="button" data-testid="admin-mobile-close" className="asb-mobile-close" onClick={() => setMobileOpen(false)} aria-label="Close menu">
            <X size={16} />
          </button>
        </div>

        <nav ref={navRef} className="asb-nav" data-testid="admin-sidebar-nav">
          {ITEMS.map((it, idx) => {
            if (it.divider) {
              return (
                <div key={`div-${idx}`} className="asb-divider">
                  <span className="asb-divider-label">{it.divider}</span>
                  <span className="asb-divider-line" />
                </div>
              );
            }
            const Icon = it.icon;
            const badge = it.badgeKey === "failed" ? failedCount : 0;
            const testid = it.tab ? `admin-tab-${it.tab}` : `admin-nav-${it.to.replace(/\//g, "-").slice(1)}`;
            return (
              <Link key={it.to} to={it.to} data-testid={testid} title={collapsed ? it.label : undefined}
                target={it.external ? "_blank" : undefined} rel={it.external ? "noreferrer" : undefined}
                onMouseDown={it.external ? undefined : onNavLinkMouseDown}
                onTouchStart={it.external ? undefined : onNavLinkMouseDown}
                className={`asb-item ${isActive(it) ? "is-active" : ""}`}>
                <span className="asb-item-ico"><Icon size={17} /></span>
                <span className="asb-item-label">{it.label}</span>
                {badge > 0 && <span className="asb-item-badge" data-testid={`asb-badge-${it.tab}`}>{badge}</span>}
              </Link>
            );
          })}
        </nav>

        <div className="asb-foot">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="profile-avatar-btn" className="asb-profile" aria-label="Open profile menu">
                <img src="/bni-logo.png" alt="" className="asb-profile-img" />
                <span className="asb-profile-dot" />
                <span className="asb-profile-text">
                  <span className="asb-profile-name">{user?.name || "Admin"}</span>
                  <span className="asb-profile-role">Admin · Davangere</span>
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align={collapsed ? "start" : "end"} side="right" sideOffset={10}
              className="!bg-[var(--panel)] !border-[var(--hairline)] !rounded-none min-w-[240px] !p-0">
              <DropdownMenuLabel className="px-3 py-3 !font-normal">
                <div className="text-sm truncate" style={{ color: "var(--text)" }}>{user?.name || "Admin"}</div>
                <div className="mono-label truncate" style={{ color: "var(--taupe)" }}>{user?.phone || "Admin · Davangere Region"}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator className="!bg-[var(--hairline)] !my-0" />
              <DropdownMenuItem data-testid="logout-menu-item" onClick={logout}
                className="!rounded-none !px-3 !py-2.5 flex items-center gap-3 cursor-pointer hover:!bg-[var(--panel-2)] focus:!bg-[var(--panel-2)]">
                <LogOut size={14} style={{ color: "var(--red)" }} />
                <span className="text-sm">Logout</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button type="button" data-testid="admin-sidebar-toggle" className="asb-collapse" onClick={() => setCollapsed(!collapsed)}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} title={collapsed ? "Expand" : "Collapse"}>
            <ChevronsLeft size={16} className="asb-collapse-ico" />
            <span className="asb-collapse-label">Collapse</span>
          </button>
        </div>
      </aside>

      <div className="admin-content">
        <div className="admin-mobile-bar">
          <button data-testid="admin-mobile-menu" className="admin-mobile-menu-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu">
            <Menu size={18} />
          </button>
          <img src="/risse-bni-logo.png" alt="RISSE 26" className="h-9 w-auto" />
        </div>
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={location.pathname + location.search}
            custom={direction}
            variants={{
              enter: (d) => ({ opacity: 0, x: d > 0 ? 48 : -48 }),
              center: { opacity: 1, x: 0 },
              exit:  (d) => ({ opacity: 0, x: d > 0 ? -32 : 32 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{
              x: { type: "tween", duration: 0.42, ease: [0.22, 1, 0.36, 1] },
              opacity: { duration: 0.26, ease: "easeOut" },
            }}
            style={{ willChange: "transform, opacity" }}
          >
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
